<script setup lang="ts">
import {
  computed,
  ref,
  nextTick,
  inject,
  toRef,
  watch,
  onBeforeUnmount,
  type Component,
} from 'vue';
import { createPopper, type Instance as Popper } from '@popperjs/core';
import type { IColumnDefinition } from './types';
import type { TableModel, RowModel } from './model';
import { useColumn, useCellFormatter } from './composables';
import { resolveEditorComponent } from './compile-template';
import {
  ItpTextEditor,
  ItpNumberEditor,
  ItpCheckboxEditor,
  ItpDateEditor,
  ItpLockEditor,
  ItpSelectEditor,
  ItpTimeEditor,
  ItpTextareaEditor,
} from './cell-types';

const props = defineProps<{
  row: RowModel;
  column: IColumnDefinition;
  editing?: boolean;
  autofocus?: boolean;
}>();

const model = inject<TableModel>('model');
if (!model) {
  throw new Error('ItpDataTableCell missing model injection');
}

function getDefaultEditorByType(type: string | undefined): Component {
  switch (type) {
    case 'number':
      return ItpNumberEditor;
    case 'date':
      return ItpDateEditor;
    case 'time':
      return ItpTimeEditor;
    case 'checkbox':
      return ItpCheckboxEditor;
    case 'lock':
      return ItpLockEditor;
    case 'select':
    case 'select2':
      return ItpSelectEditor;
    case 'textarea':
      return ItpTextareaEditor;
    case 'link':
    case 'template':
    case 'text':
    default:
      return ItpTextEditor;
  }
}

function getEditorTextAlign(editor: Component): string | undefined {
  if (editor === ItpNumberEditor) {
    return 'right';
  }
  if (editor === ItpCheckboxEditor) {
    return 'center';
  }
  if (editor === ItpTextEditor) {
    return 'left';
  }
  return undefined;
}

const { formatter: formatterComponent, isCustom: isCustomFormatter } =
  useCellFormatter(toRef(props, 'column'));

const resolvedCustomEditor = computed(() =>
  resolveEditorComponent(props.column.editor),
);

const editorComponent = computed<Component>(
  () => resolvedCustomEditor.value ?? getDefaultEditorByType(props.column.type),
);

const isCustomEditor = computed(() => !!resolvedCustomEditor.value);

const cellValue = computed(() => {
  return props.row.getValue(props.column.field);
});

const isEditable = computed(
  () =>
    props.column.editable !== false &&
    !props.column.readonly &&
    !props.column.systemColumn,
);

const showEditor = computed(() => {
  return props.editing && isEditable.value;
});

const { columnClasses, columnStyle, displayCellHeader, cellHeaderClass } =
  useColumn({
    column: props.column,
    options: model.options,
    editing: toRef(props, 'editing', false),
    editorTextAlign: () =>
      showEditor.value && !isCustomEditor.value
        ? getEditorTextAlign(editorComponent.value)
        : undefined,
  });

const errorMessage = computed(() => props.row.errors.value[props.column.field]);

const finalCellClasses = computed(() => {
  const classes = { ...columnClasses.value };

  classes['itp-datatable__table-cell'] = true;
  classes['position-relative'] = true;

  if (errorMessage.value && props.editing) {
    classes['has-error'] = true;
  }
  return classes;
});

const globs = computed(() => model.options.globs || {});
const messages = computed(() => model.options.messages || {});

const onUpdateValue = (newValue: any) => {
  props.row.setValue(props.column.field, newValue);
};

const onEditorChange = (newValue: any) => {
  const field = props.column.field;
  model.emitCellEvent(`${field}:change`, {
    col: props.column,
    row: props.row,
    field,
    value: newValue,
  });
};

const editorRef = ref<any>(null);
const focus = () => {
  nextTick(() => {
    if (editorRef.value?.focus) {
      editorRef.value.focus();
    } else if (editorRef.value?.$el?.focus) {
      editorRef.value.$el.focus();
      const input = editorRef.value.$el.querySelector(
        'input, select, textarea',
      );
      if (input) {
        input.focus();
      }
    }
  });
};
defineExpose({ focus });

const cellRef = ref<HTMLElement | null>(null);
const errorTooltipRef = ref<HTMLElement | null>(null);
let errorPopper: Popper | null = null;

const EDITOR_CONTROL =
  '.vs__dropdown-toggle, .mx-input-wrapper, input:not([type="hidden"]), select, textarea';

function getEditorControl(): Element | null {
  const editorEl = editorRef.value?.$el;
  if (!(editorEl instanceof Element)) {
    return null;
  }
  return editorEl.matches(EDITOR_CONTROL)
    ? editorEl
    : (editorEl.querySelector(EDITOR_CONTROL) ?? editorEl);
}

watch(
  errorTooltipRef,
  (tooltip) => {
    errorPopper?.destroy();
    errorPopper = null;
    const anchor = getEditorControl() ?? cellRef.value;
    if (tooltip && anchor) {
      errorPopper = createPopper(anchor, tooltip, {
        placement: 'bottom-start',
        strategy: 'fixed',
      });
    }
  },
  { flush: 'post' },
);

onBeforeUnmount(() => errorPopper?.destroy());
</script>

<template>
  <td
    ref="cellRef"
    :class="finalCellClasses"
    :style="columnStyle"
    :title="
      !showEditor && cellValue != null && typeof cellValue !== 'object'
        ? String(cellValue)
        : undefined
    "
  >
    <div
      v-if="displayCellHeader"
      class="cell-header text-muted text-uppercase"
      :class="cellHeaderClass"
    >
      <small>{{ column.header }}</small>
    </div>

    <template v-if="showEditor">
      <component
        :is="editorComponent"
        v-if="isCustomEditor"
        ref="editorRef"
        :model-value="cellValue"
        :row="row.data"
        :row-model="row"
        :column="column"
        :table="model"
        :globs="globs"
        :messages="messages"
        :autofocus="autofocus"
        @update:model-value="onUpdateValue"
      />
      <component
        :is="editorComponent"
        v-else
        ref="editorRef"
        :model-value="cellValue"
        :row="row.data"
        :column="column"
        :autofocus="autofocus"
        @update:model-value="onUpdateValue"
        @change="onEditorChange"
      />
      <div
        v-if="errorMessage"
        ref="errorTooltipRef"
        class="invalid-tooltip d-block"
        role="alert"
      >
        {{ errorMessage }}
      </div>
    </template>

    <template v-else>
      <component
        :is="formatterComponent"
        v-if="isCustomFormatter"
        :value="cellValue"
        :row="row.data"
        :column="column"
        :globs="globs"
        :messages="messages"
      />
      <component
        :is="formatterComponent"
        v-else
        :value="cellValue"
        :row="row.data"
        :column="column"
      />
    </template>
  </td>
</template>
